import React, { useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./AdminOperations.css";
import { deleteUser, routeFindUserByRol, routeUpdateUser } from "./constants";

export const UpdateUser = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [rolActual, setRolActual] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [cargandoUsuarios, setCargandoUsuarios] = useState(false);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [email, setEmail] = useState("");
  const [nombre, setNombre] = useState("");
  const [apellido, setApellido] = useState("");
  const [imagen, setImagen] = useState("");
  const [contraseña, setContraseña] = useState("");
  const [matricula, setMatricula] = useState("");
  const [telefono, setTelefono] = useState("");
  const [tieneCertificado, setTieneCertificado] = useState(false);
  const [accionEnProceso, setAccionEnProceso] = useState(null);
  const [resultado, setResultado] = useState(null);
  const navigate = useNavigate();

  const usuariosFiltrados = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    if (!termino) {
      return usuarios;
    }

    return usuarios.filter((usuario) =>
      [usuario.nombre, usuario.apellido, usuario.email, usuario.matricula].some(
        (valor) =>
          String(valor || "")
            .toLowerCase()
            .includes(termino),
      ),
    );
  }, [busqueda, usuarios]);

  const handleConsultarUsuarios = async (rolSeleccionado) => {
    setCargandoUsuarios(true);
    setRolActual(rolSeleccionado);
    setUsuarioSeleccionado(null);
    setResultado(null);

    try {
      const response = await axios.get(routeFindUserByRol, {
        params: { role: rolSeleccionado },
      });
      const lista = response.data?.data ?? response.data ?? [];
      setUsuarios(Array.isArray(lista) ? lista : []);
    } catch (error) {
      console.error("Error al consultar usuarios:", error);
      setUsuarios([]);
      setResultado({
        tipo: "error",
        mensaje: "No fue posible consultar los usuarios.",
      });
    } finally {
      setCargandoUsuarios(false);
    }
  };

  const editarUsuario = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setEmail(usuario.email || "");
    setNombre(usuario.nombre || "");
    setApellido(usuario.apellido || "");
    setImagen(usuario.imagen || "");
    setMatricula(usuario.matricula || "");
    setContraseña(usuario.contraseña || "");
    setTelefono(usuario.telefono || "");
    setTieneCertificado(Boolean(usuario.tiene_certificado));
    setResultado(null);
  };

  const cancelarEdicion = () => {
    setUsuarioSeleccionado(null);
    setResultado(null);
  };

  const actualizarUsuario = async (event) => {
    event.preventDefault();

    if (!usuarioSeleccionado || accionEnProceso !== null) {
      return;
    }

    setAccionEnProceso("actualizar");
    setResultado(null);

    try {
      const payload = {
        id: usuarioSeleccionado.id,
        email,
        nombre,
        apellido,
        telefono,
        imagen,
        contraseña,
        matricula,
        tiene_certificado: tieneCertificado,
      };
      const response = await axios.put(routeUpdateUser, payload);

      setUsuarios((listaActual) =>
        listaActual.map((usuario) =>
          usuario.id === usuarioSeleccionado.id
            ? { ...usuario, ...payload }
            : usuario,
        ),
      );
      setUsuarioSeleccionado(null);
      setResultado({
        tipo: "success",
        mensaje:
          response.data?.message || "El usuario se actualizó correctamente.",
      });
    } catch (error) {
      console.error("Error al actualizar usuario:", error);
      setResultado({
        tipo: "error",
        mensaje:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "No fue posible actualizar el usuario.",
      });
    } finally {
      setAccionEnProceso(null);
    }
  };

  const eliminarUsuario = async (usuario) => {
    const confirmado = window.confirm(
      `¿Deseas eliminar a ${usuario.nombre} ${usuario.apellido}? Esta acción también puede eliminar información relacionada.`,
    );
    if (!confirmado || accionEnProceso !== null) {
      return;
    }

    setAccionEnProceso(usuario.id);
    setResultado(null);

    try {
      const response = await axios.delete(deleteUser, {
        params: { email: usuario.email, rol: usuario.rol || rolActual },
        headers: { "Content-Type": "application/json" },
      });

      setUsuarios((listaActual) =>
        listaActual.filter((item) => item.id !== usuario.id),
      );
      if (usuarioSeleccionado?.id === usuario.id) {
        setUsuarioSeleccionado(null);
      }
      setResultado({
        tipo: "success",
        mensaje:
          response.data?.message || "El usuario se eliminó correctamente.",
      });
    } catch (error) {
      console.error("Error al eliminar usuario:", error);
      setResultado({
        tipo: "error",
        mensaje:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "No fue posible eliminar el usuario.",
      });
    } finally {
      setAccionEnProceso(null);
    }
  };

  return (
    <div className="admin-operation-page">
      <main className="admin-operation-container">
        <header className="admin-operation-header">
          <p className="admin-operation-eyebrow">Administración de usuarios</p>
          <h1>Actualizar usuarios</h1>
          <p>
            Consulta maestros o alumnos, selecciona un registro para editarlo y
            administra las cuentas desde un solo lugar.
          </p>
        </header>

        <section className="admin-operation-panel">
          <div className="admin-operation-panel-heading">
            <div>
              <h2>Selecciona el tipo de usuario</h2>
              <p>El listado se actualizará con el rol elegido.</p>
            </div>
            <div className="admin-operation-toolbar-actions">
              <button
                className="admin-operation-button"
                type="button"
                onClick={() => handleConsultarUsuarios("Maestro")}
                disabled={cargandoUsuarios || accionEnProceso !== null}
              >
                Consultar maestros
              </button>
              <button
                className="admin-operation-button"
                type="button"
                onClick={() => handleConsultarUsuarios("alumno")}
                disabled={cargandoUsuarios || accionEnProceso !== null}
              >
                Consultar alumnos
              </button>
              <button
                className="admin-operation-button admin-operation-button-secondary"
                type="button"
                onClick={() => navigate("/admin-home")}
                disabled={accionEnProceso !== null}
              >
                Regresar al inicio
              </button>
            </div>
          </div>

          {resultado && (
            <div
              className={`admin-operation-status admin-operation-status-${resultado.tipo}`}
              role="status"
            >
              {resultado.mensaje}
            </div>
          )}

          {rolActual && (
            <>
              <div className="admin-operation-toolbar">
                <input
                  className="admin-operation-search"
                  type="search"
                  placeholder="Buscar por nombre, correo o matrícula"
                  value={busqueda}
                  onChange={(event) => setBusqueda(event.target.value)}
                  aria-label="Buscar usuario"
                />
                <p className="admin-operation-count">
                  {cargandoUsuarios
                    ? "Consultando usuarios..."
                    : `${usuariosFiltrados.length} resultado${usuariosFiltrados.length === 1 ? "" : "s"}`}
                </p>
              </div>

              {!cargandoUsuarios && usuariosFiltrados.length === 0 ? (
                <p className="admin-operation-empty">
                  No se encontraron usuarios para este rol.
                </p>
              ) : (
                <div className="admin-operation-table-wrapper">
                  <table className="admin-operation-table">
                    <thead>
                      <tr>
                        <th>Usuario</th>
                        <th>Contacto</th>
                        <th>Matrícula</th>
                        <th>Constancia</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usuariosFiltrados.map((usuario) => (
                        <tr key={usuario.id ?? usuario.email}>
                          <td>
                            <div className="admin-operation-table-actions">
                              {usuario.imagen ? (
                                <img
                                  className="admin-operation-avatar"
                                  src={usuario.imagen}
                                  alt=""
                                />
                              ) : (
                                <span className="admin-operation-avatar" />
                              )}
                              <div>
                                <strong>
                                  {usuario.nombre} {usuario.apellido}
                                </strong>
                                <div>{usuario.rol || rolActual}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>{usuario.email}</div>
                            <div>{usuario.telefono || "Sin teléfono"}</div>
                          </td>
                          <td>{usuario.matricula}</td>
                          <td>
                            <span className="admin-operation-badge">
                              {usuario.tiene_certificado
                                ? "Otorgada"
                                : "Pendiente"}
                            </span>
                          </td>
                          <td>
                            <div className="admin-operation-table-actions">
                              <button
                                className="admin-operation-button admin-operation-button-compact"
                                type="button"
                                onClick={() => editarUsuario(usuario)}
                                disabled={accionEnProceso !== null}
                              >
                                Editar
                              </button>
                              <button
                                className="admin-operation-button admin-operation-button-danger admin-operation-button-compact"
                                type="button"
                                onClick={() => eliminarUsuario(usuario)}
                                disabled={accionEnProceso !== null}
                              >
                                {accionEnProceso === usuario.id
                                  ? "Eliminando..."
                                  : "Eliminar"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>

        {usuarioSeleccionado && (
          <form className="admin-operation-panel" onSubmit={actualizarUsuario}>
            <div className="admin-operation-panel-heading">
              <div>
                <h2>Editar usuario</h2>
                <p>
                  Modifica los datos de {usuarioSeleccionado.nombre}{" "}
                  {usuarioSeleccionado.apellido}.
                </p>
              </div>
              <span className="admin-operation-badge">
                {usuarioSeleccionado.matricula}
              </span>
            </div>

            <div className="admin-operation-form-grid">
              <div className="admin-operation-field">
                <label htmlFor="update-user-email">Correo electrónico</label>
                <input
                  id="update-user-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={accionEnProceso !== null}
                  required
                />
              </div>
              <div className="admin-operation-field">
                <label htmlFor="update-user-id">Matrícula</label>
                <input
                  id="update-user-id"
                  type="text"
                  value={matricula}
                  onChange={(event) => setMatricula(event.target.value)}
                  disabled={accionEnProceso !== null}
                  required
                />
              </div>
              <div className="admin-operation-field">
                <label htmlFor="update-user-name">Nombre</label>
                <input
                  id="update-user-name"
                  type="text"
                  value={nombre}
                  onChange={(event) => setNombre(event.target.value)}
                  disabled={accionEnProceso !== null}
                  required
                />
              </div>
              <div className="admin-operation-field">
                <label htmlFor="update-user-lastname">Apellido</label>
                <input
                  id="update-user-lastname"
                  type="text"
                  value={apellido}
                  onChange={(event) => setApellido(event.target.value)}
                  disabled={accionEnProceso !== null}
                  required
                />
              </div>
              <div className="admin-operation-field">
                <label htmlFor="update-user-phone">Teléfono</label>
                <input
                  id="update-user-phone"
                  type="tel"
                  value={telefono}
                  onChange={(event) => setTelefono(event.target.value)}
                  disabled={accionEnProceso !== null}
                />
              </div>
              <div className="admin-operation-field">
                <label htmlFor="update-user-image">URL de imagen</label>
                <input
                  id="update-user-image"
                  type="url"
                  value={imagen}
                  onChange={(event) => setImagen(event.target.value)}
                  disabled={accionEnProceso !== null}
                />
              </div>
              <div className="admin-operation-field">
                <label htmlFor="update-user-password">Contraseña</label>
                <input
                  id="update-user-password"
                  type="password"
                  value={contraseña}
                  onChange={(event) => setContraseña(event.target.value)}
                  disabled={accionEnProceso !== null}
                  required
                />
              </div>
              <div className="admin-operation-field">
                <label htmlFor="update-user-certificate">Constancia</label>
                <select
                  id="update-user-certificate"
                  value={tieneCertificado ? "true" : "false"}
                  onChange={(event) =>
                    setTieneCertificado(event.target.value === "true")
                  }
                  disabled={accionEnProceso !== null}
                >
                  <option value="false">Pendiente</option>
                  <option value="true">Otorgada</option>
                </select>
              </div>
            </div>

            <div className="admin-operation-actions">
              <button
                className="admin-operation-button"
                type="submit"
                disabled={accionEnProceso !== null}
              >
                {accionEnProceso === "actualizar"
                  ? "Actualizando..."
                  : "Guardar cambios"}
              </button>
              <button
                className="admin-operation-button admin-operation-button-secondary"
                type="button"
                onClick={cancelarEdicion}
                disabled={accionEnProceso !== null}
              >
                Cancelar edición
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
};
